// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type OperationType } from '@shared/data-access/interfaces/api/ai/expected-trade';
import { type OrderStrategy } from '@shared/data-access/interfaces/api/ai/portfolio-rebalance';

export const assetAllocationAssetClassPayload = {
  asset_category: '',
  asset_class: '',
  search_text: '',
};

export const baseHoldingPayload = {
  date: '',
  holding_id: '',
  isin: '',
  ticker: '',
  unrealized_capital_gains: 0,
};

export const tradePayload = {
  ...baseHoldingPayload,
  currency: null,
  name: '',
  operation: '' as OperationType,
  realized_capital_gains: 0,
  volume: 0,
};
export const holdingsPayload = {
  ...baseHoldingPayload,
  qty: 0,
  price: 0,
};

export const targetAssetPayload = {
  ticker: '',
  weight: 0,
};

export const screenerStockSummaryPayload = {
  description: '',
  esg_exclusion: [''],
  esg_not_informed: false,
  esg_scoring: {
    filter: [''],
    lower: [0],
    upper: [0],
  },
  factors_filter: {
    factor: [''],
    lower: [0],
    upper: [0],
  },
  sectors_filter: [''],
  sub_universe: [''],
  to_exclude: [''],
  to_include: [''],
  universe_base: {
    description: '',
    name: '',
  },
};

export const portfolioPayload = {
  benchmark: {
    target_asset: [targetAssetPayload],
  },
  target_asset: [targetAssetPayload],
};

export const rebalanceDepositPayload = {
  holdings: [
    {
      ...holdingsPayload,
      currency: '',
      name: '',
    },
  ],
  target_strategy: {
    target_asset: [
      {
        ...targetAssetPayload,
        currency: null,
        isin: '',
        name: '',
      },
    ],
  },
};

export const rebalanceWithdrawalsPayload = {
  ...rebalanceDepositPayload,
  withdrawal_opt: {
    order_strategy: '' as OrderStrategy,
    target_error: 0,
    withdrawal_boundaries: [0, 0],
  },
};

export const portfolioStrategyPayload = {
  benchmark: {
    target_asset: [
      {
        ...targetAssetPayload,
        isin: '',
      },
    ],
  },
  holdings: [holdingsPayload],
};

export const rebalanceTradingPayload = {
  ...rebalanceDepositPayload,
  trades: [tradePayload],
};

export const rebalanceGeneralPayload = {
  ...rebalanceDepositPayload,
  additional_amount: 0,
  transfers: false,
  withdrawal_opt: {
    order_strategy: '' as OrderStrategy,
    target_error: 0,
    withdrawal_boundaries: [0, 0],
  },
};

export const rebalanceTradePayload = {
  ...tradePayload,
  from_ticker: undefined,
  from_isin: undefined,
  to_ticker: undefined,
  to_isin: undefined,
};

export const reportingTargetPayload = {
  ...rebalanceDepositPayload,
  trades: [rebalanceTradePayload],
};

export const reportingTaxesPayload = {
  trades: [rebalanceTradePayload],
  tax_config: {
    long_term_ratio: 0,
    short_term_date: '',
    short_term_ratio: 0,
  },
};

export const simulationPayload = {
  agg_period: 0,
  cashflow_contribution: 0,
  expected_returns: 0,
  expected_volatility: 0,
  initial_amount: 0,
  n_periods: 0,
  n_scenarios: 0,
  quantiles: [0],
  risk_free_rate: 0,
};
export const montecarloInsightsPayload = {
  montecarlo_settings: simulationPayload,
  montecarlo_quantiles: [
    {
      average_returns: 0,
      cagr: 0,
      max_drawdawns: 0,
      max_drawdown_exc_cf: 0,
      quantile: 0,
      sharpe: 0,
      sortino: 0,
      total_position: [0],
      volatility: 0,
    },
  ],
};

const historicalData = {
  end_date: '',
  start_date: '',
};

const assetAnalysis = {
  historical_data: historicalData,
  returns: {
    bnch_returns_1M: 0,
    bnch_returns_3M: 0,
    bnch_returns_6M: 0,
    returns_1M: 0,
    returns_3M: 0,
    returns_6M: 0,
    returns_1Y: 0,
    returns_3Y: 0,
    returns_5Y: 0,
    returns_10Y: 0,
    returns_YTD: 0,
    returns_max: 0,
    bnch_returns_1Y: 0,
    bnch_returns_3Y: 0,
    bnch_returns_5Y: 0,
    bnch_returns_10Y: 0,
    bnch_returns_YTD: 0,
    bnch_returns_max: 0,
    CAGR: 0,
    bnch_CAGR: 0,
  },
  risk: {
    alpha: 0,
    beta: 0,
    bnch_sharpe: 0,
    bnch_volatility: 0,
    sharpe: 0,
    volatility: 0,
    max_drawdown: 0,
    bnch_max_drawdown: 0,
  },
  ticker: '',
  var: {
    quantile: [1],
    value: [0],
  },
};

const assetBacktest = {
  historical_data: historicalData,
  ticker: '',
  ts_backtest: {
    benchmark: [1],
    date: [''],
    portfolio: [1],
  },
  ts_drawdowns: {
    benchmark: [0],
    date: [''],
    portfolio: [0],
  },
};

export const comparisonInsightsPayload = {
  asset_analysis: [assetAnalysis],
  asset_backtest: [assetBacktest],
  corr_matrix: {
    correlation_cols: {
      BND: [0],
    },
    ticker: [''],
  },
  historical_data: historicalData,
  risk_returns: [{ returns: 0, sharpe: 0, ticker: '', volatility: 0 }],
};

export const etfSearchPayload = {
  asset_class: [''],
  asset_manager: [''],
  benchmark: [''],
  currency: [''],
  investment_segment: [''],
  location: [''],
  max_aum: 0,
  max_expenses_ratio: 0,
  min_aum: 0,
  min_expenses_ratio: 0,
  text_search: '',
};

export const mutualFundsPayload = {
  ...etfSearchPayload,
  category: [''],
  country_exposure: [
    {
      exposure: '',
      max_weight: 0,
      min_weight: 0,
    },
  ],
  max_nav: 0,
  min_nav: 0,
  sector_exposure: [
    {
      exposure: '',
      max_weight: 0,
      min_weight: 0,
    },
  ],
  segment: [''],
};

export const traderOrderPayload = {
  trades: [tradePayload],
  latest_prices: [
    {
      price: 0,
      ticker: '',
    },
  ],
};
